import { Injectable } from "@angular/core";
import { Item } from "lib/composites";
import {
	ApplicationDocumentType,
	ConcessionType,
	EmploymentStatus,
	GuarantorPolicy,
	Parking,
	SourceOfIncome,
	VaccinationType,
} from "src/app/schema";

@Injectable({
	providedIn: "root",
})
export class ConfigurationService {
	public readonly contactSourceOptions: Item[] = [
		{ label: "Walk In", value: "Walk In" },
		{ label: "Phone", value: "Phone Call" },
		{ label: "Email", value: "Email" },
		{ label: "Other", value: "Other" },
	];

	public readonly petOptions: Item[] = [
		{ label: "Dog", value: "Dog" },
		{ label: "Cat", value: "Cat" },
	];

	public readonly referralSourcesOptions: Item[] = [
		{ label: "Waitlist", value: "Waitlist" },
		{ label: "Resident", value: "Resident" },
		{ label: "Building Sign", value: "Building Sign" },
		{ label: "Website", value: "Website" },
		{ label: "Google", value: "Google" },
		{ label: "Broker", value: "Broker" },
		{ label: "WOM", value: "WOM" },
	];

	public readonly reasonsForLookingOptions: Item[] = [
		{ label: "Job Relocation", value: "Job Relocation" },
		{ label: "Need more Space", value: "Need more Space" },
		{ label: "Need less Space", value: "Need less Space" },
		{ label: "Rent vs Own", value: "Rent vs Own" },
		{ label: "Lifestyle Change", value: "Lifestyle Change" },
		{ label: "Location", value: "Location" },
		{ label: "Pied-à-terre", value: "Pied-à-terre" },
		{ label: "Amenities", value: "Amenities" },
		{ label: "New Construction", value: "New Construction" },
		{ label: "Corporate", value: "Corporate" },
		{ label: "For Child", value: "For Child" },
		{ label: "Save Money/Spend Less", value: "Save Money/Spend Less" },
	];

	public readonly maritalStatusOptions: Item[] = [
		{ label: "Single", value: "Single" },
		{ label: "Married (Without Child)", value: "MarriedNoChild" },
		{ label: "Family with Children", value: "FamilyWithChildren" },
		{ label: "Couple Dating", value: "CoupleDating" },
		{ label: "Roommates", value: "Roommates" },
		{ label: "Empty Nester", value: "EmptyNester" },
	];

	public readonly presentResidenceOptions: Item[] = [
		{ label: "Own", value: "Own" },
		{ label: "Rent", value: "Rent" },
		{ label: "Family", value: "Family" },
		{ label: "Corporate Housing", value: "Corporate Housing" },
	];

	public readonly leaseTermOptions: Item[] = [
		{ label: "3 months", value: 3 },
		{ label: "4 months", value: 4 },
		{ label: "5 months", value: 5 },
		{ label: "6 months", value: 6 },
		{ label: "7 months", value: 7 },
		{ label: "8 months", value: 8 },
		{ label: "9 months", value: 9 },
		{ label: "10 months", value: 10 },
		{ label: "11 months", value: 11 },
		{ label: "12 months", value: 12 },
		{ label: "13 months", value: 13 },
		{ label: "14 months", value: 14 },
		{ label: "15 months", value: 15 },
		{ label: "16 months", value: 16 },
		{ label: "17 months", value: 17 },
		{ label: "18 months", value: 18 },
		{ label: "19 months", value: 19 },
		{ label: "20 months", value: 20 },
		{ label: "21 months", value: 21 },
		{ label: "22 months", value: 22 },
		{ label: "23 months", value: 23 },
		{ label: "24 months", value: 24 },
		{ label: "25 months", value: 25 },
		{ label: "26 months", value: 26 },
	];

	public readonly parkingOptions: Item[] = [
		{ label: "No Parking", value: Parking.NoParking },
		{ label: "Parking Spot", value: Parking.ParkingSpot },
		{ label: "Garage", value: Parking.Garage },
		{ label: "Valet", value: Parking.Valet },
		{ label: "Carport", value: Parking.Carport },
		{ label: "Street Parking", value: Parking.StreetParking },
	];

	public readonly concessionOptions: Item[] = [
		{ label: "None", value: 0 },
	].concat(
		[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4].map((value) => {
			return { label: `${value} Months Free`, value: value };
		}),
	);

	public readonly concessionTypeOptions: Item[] = [
		{ label: "None", value: ConcessionType.None },
		{ label: "Fixed Amount", value: ConcessionType.Amount },
		{ label: "Duration", value: ConcessionType.Duration },
	];

	public readonly commissionOptions: Item[] = [
		{ label: "None", value: 0 },
	].concat(
		[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4].map((value) => {
			return { label: `${value} Months Rent`, value: value };
		}),
	);

	public readonly minIncomeReqRange: Item[] = [35, 36, 37, 38, 39, 40]
		.map((value) => {
			return { label: `${value}x Monthly Rent`, value: value };
		})
		.concat([{ label: "None", value: 0 }]);

	public readonly incomeSourceOptions: Item[] = [
		{ label: "Employment", value: SourceOfIncome.Employment },
		{ label: "Social Security", value: SourceOfIncome.SocialSecurity },
		{ label: "Housing Vouchers", value: SourceOfIncome.HousingVouchers },
		{ label: "Other", value: SourceOfIncome.Other },
	];

	public readonly employmentStatusOptions: Item[] = [
		{ label: "Employed", value: EmploymentStatus.Employed },
		{ label: "Self-employed", value: EmploymentStatus.SelfEmployed },
		{ label: "Unemployed", value: EmploymentStatus.Unemployed },
		{ label: "Retired", value: EmploymentStatus.Retired },
		{ label: "Student", value: EmploymentStatus.Student },
		{ label: "Other", value: EmploymentStatus.Other },
	];

	public readonly occupationOptions: Item[] = [
		{ label: "Accountant", value: "accountant" },
		{ label: "Architect", value: "architect" },
		{ label: "Attorney", value: "attorney" },
		{ label: "Banker", value: "banker" },
		{ label: "Business owner", value: "businessOwner" },
		{ label: "Doctor", value: "doctor" },
		{ label: "Engineer", value: "engineer" },
		{ label: "Entrepreneur", value: "entrepreneur" },
		{ label: "Financial analyst", value: "financialAnalyst" },
		{ label: "Financial planner", value: "financialPlanner" },
		{ label: "Investor", value: "investor" },
		{ label: "Lawyer", value: "lawyer" },
		{ label: "Manager", value: "manager" },
		{ label: "Marketing", value: "marketing" },
		{ label: "Nurse", value: "nurse" },
		{ label: "Other", value: "other" },
		{ label: "Pharmacist", value: "pharmacist" },
		{ label: "Physician", value: "physician" },
		{ label: "Professor", value: "professor" },
		{ label: "Real estate agent", value: "realEstateAgent" },
		{ label: "Real estate investor", value: "realEstateInvestor" },
		{ label: "Sales", value: "sales" },
		{ label: "Teacher", value: "teacher" },
		{ label: "Technician", value: "technician" },
		{ label: "Trader", value: "trader" },
		{ label: "Veterinarian", value: "veterinarian" },
		{ label: "Writer", value: "writer" },
	];

	public readonly guarantorPolicies: Item[] = [
		{ value: GuarantorPolicy.Allowed, label: "Allowed" },
		{ value: GuarantorPolicy.NotAllowed, label: "Not Allowed" },
		{ value: GuarantorPolicy.Student, label: "Allowed for Students Only" },
	];

	public readonly petVaccinations: Item[] = [
		{ value: VaccinationType.CanineParvovirus, label: "Canine Parvovirus" },
		{ value: VaccinationType.CanineDistemper, label: "Canine Distemper" },
		{ value: VaccinationType.Hepatitis, label: "Hepatitis" },
		{ value: VaccinationType.Rabies, label: "Rabies" },
		{ value: VaccinationType.Bordetella, label: "Bordetella" },
		{ value: VaccinationType.CanineInfluenza, label: "Canine Influenza" },
		{ value: VaccinationType.LeptoSpirosis, label: "Lepto Spirosis" },
		{ value: VaccinationType.LymeDisease, label: "Lyme Disease" },
	];

	public readonly applicationDocumentTypes: Item[] = [
		{ value: ApplicationDocumentType.W9, label: "W9" },
		{ value: ApplicationDocumentType.BankStatement, label: "Bank Statement" },
		{ value: ApplicationDocumentType.PayStub, label: "Pay Stub" },
		{ value: ApplicationDocumentType.PetVaccination, label: "Pet Vaccination" },
		{ value: ApplicationDocumentType.Other, label: "Other" },
	];

	public readonly petDocumentTypes: Item[] = [
		{ value: ApplicationDocumentType.PetVaccination, label: "Pet Vaccination" },
		{
			value: ApplicationDocumentType.PetAttestation,
			label: "Service Animal Attestation",
		},
		{ value: ApplicationDocumentType.Other, label: "Other" },
	];

	public readonly unitSizeOptions: Item[] = [
		{ value: 0, label: "Studio" },
		{ value: 0.5, label: "Alcove" },
		{ value: 1, label: "1 Bedroom" },
		{ value: 1.5, label: "1 Bedroom + Den" },
		{ value: 2, label: "2 Bedroom" },
		{ value: 2.5, label: "2 Bedroom + Den" },
		{ value: 3, label: "3 Bedroom" },
	];

	public readonly exposureOptions: Item[] = [
		{ value: "N", label: "North" },
		{ value: "NE", label: "North-East" },
		{ value: "E", label: "East" },
		{ value: "SE", label: "South-East" },
		{ value: "S", label: "South" },
		{ value: "SW", label: "South-West" },
		{ value: "W", label: "West" },
		{ value: "NW", label: "North-West" },
	];

	public readonly emergencyContactRelationshipOptions: Item[] = [
		{ label: "Spouse", value: "Spouse" },
		{ label: "Parent", value: "Parent" },
		{ label: "Sibling", value: "Sibling" },
		{ label: "Child", value: "Child" },
		{ label: "Friend", value: "Friend" },
		{ label: "Other", value: "Other" },
	];

	public readonly identificationTypeOptions: Item[] = [
		{ label: "None", value: "None" },
		{ label: "SSN", value: "SSN" },
		{ label: "ITIN", value: "ITIN" },
	];

	public readonly referralSourceOptions: Item[] = [
		{ label: "Waitlist", value: "Waitlist" },
		{ label: "Resident", value: "Resident" },
		{ label: "Building Sign", value: "Building Sign" },
		{ label: "Website", value: "Website" },
		{ label: "Google", value: "Google" },
		{ label: "Broker", value: "Broker" },
		{ label: "WOM", value: "WOM" },
	];
}
